/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Inject, Injectable, Optional } from '@angular/core';
import { defer, fromEvent, merge, Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { LoggingService, Logger } from 'crimson/lib/logging/logging.service';
import { NvHigConfig, NV_HIG_CONFIG } from '../../hig.config';
import { FocusHistoryQueueService } from './focus/focus-history-queue.service';
import { isHorizontalDir } from './spatial-navigation';
import { SpatialNavigation } from './spatial-navigation-polyfill';
import { ContainFocusArgs, SpatialNavigationConfig, SpatialNavigationDirection } from './spatial-navigation.types';

@Injectable({
    providedIn: 'root'
})
export class SpatialNavigationService {
    private spatialNavigation: SpatialNavigation = undefined;
    private _navigate: any = undefined;
    private config: SpatialNavigationConfig;
    private logger: Logger;
    get enabled(): boolean {
        return this._enabled;
    }

    private _enabled = false;

    constructor(
        @Optional() @Inject(NV_HIG_CONFIG) higConfig: NvHigConfig,
        private focusHistoryQueueService: FocusHistoryQueueService,
        private logginService: LoggingService
    ) {
        this.config = higConfig?.spatialNavigationConfig ?? {};
        this.spatialNavigation = (window as any).__spatialNavigation__;
        this.logger = this.logginService.getLogger('SpatialNavigationService');
        if (this.spatialNavigation) {
            this._navigate = (window as any).navigate;
            this.spatialNavigation.keyMode = 'NONE'; // Disabling the default behavior of the window to allow manual activation of spatial navigation when desired
        }
        this.logger.info(
            `Constructor: this.spatialNavigation=${!!this
                .spatialNavigation}, window.__spatialNavigation__=${!!window.__spatialNavigation__}`
        );
    }

    // Sets the special spatial navigation container properties, see:
    // https://drafts.csswg.org/css-nav-1/#container
    // For how to choose the suitable action, please check this guide
    // ui/libraries/hig/src/lib/services/spatial-and-back-navigation.md#how-to-choose-navigateAction-among-auto-focus-and-scroll
    setNavigationContainerCssStyles(
        element: HTMLElement,
        navigateFunction: 'grid' | 'normal',
        navigateAction: 'auto' | 'focus' | 'scroll',
        enableSmoothScrolling: boolean = true
    ) {
        if (!this.enabled) {
            return;
        }

        // Set the custom "data-spatial-navigation-*" HTML attributes instead of custom "--spatial-navigation-*"
        // CSS attributes stated in the polyfill's draft.
        // The reason is that child elements in non-Chrome environments inherit custom CSS attributes from parent nodes.
        // The navigation algorithm may read the wrong css values from the child element and causes unexpected behavior.
        element.setAttribute('data-spatial-navigation-contain', 'contain');
        element.setAttribute('data-spatial-navigation-action', navigateAction);
        element.style.setProperty('--spatial-navigation-function', navigateFunction);

        // FIXME: this line of code causes a conflict with the scrolling library. here we provide an user the ability to toggle this
        // via option. we should eventually remove this line of code. AVENGERS-2313 is created to track the progress.
        if (navigateAction === 'focus' && enableSmoothScrolling) {
            element.style.setProperty('scroll-behavior', 'smooth');
        }
    }

    // Enable/disable spatial navigation feature
    enable(enable: boolean) {
        this._enabled = enable;
        this.activated = enable;

        if (this.enabled) {
            if (this.config?.focusHistoryQueueSize > 0) {
                this.focusHistoryQueueService.start(this.config.focusHistoryQueueSize);
            }
        } else {
            if (this.config?.focusHistoryQueueSize > 0) {
                this.focusHistoryQueueService.stop();
            }
        }
    }

    get activated(): boolean {
        return !!(this.spatialNavigation && this.spatialNavigation.keyMode !== 'NONE');
    }

    // Activate/deactivate spatial navigation entirely. Ineffective if the feature is disabled.
    set activated(value: boolean) {
        if (!this.enabled || !this.spatialNavigation) {
            return;
        }
        this.spatialNavigation.keyMode = value ? 'ARROW' : 'NONE';
    }

    // set the starting point for spatial navigation search..
    setStartingPoint(element: HTMLElement) {
        if (!this.enabled || !this.spatialNavigation) {
            return;
        }

        if (!element) {
            this.spatialNavigation.setStartingPoint(null);
            return;
        }

        const rect: DOMRect = element.getBoundingClientRect();
        const midx = rect.x + rect.width / 2;
        const midy = rect.y + rect.height / 2;
        this.spatialNavigation.setStartingPoint(midx, midy);
    }

    /**
     * Set the starting point on coordinates for spatial navigation search
     */
    setStartingPointAt(x: number, y: number) {
        if (!this.enabled || !this.spatialNavigation) {
            return;
        }
        this.spatialNavigation.setStartingPoint(x, y);
    }

    /**
     * perfrom a navigation search and return the best candidate
     *
     * @param source the start point element
     * @param container the element contains the candidates we want to search for
     * @param dir in what direction we want to perform the search
     * @param fallbackDirs fallback directions to search in. if specified and best candidate is not found in `dir`,
     *                     this method will continue to search in the fallback directions in order.
     * @returns the closest focusable element in the container. return null if not found.
     */
    getClosestChild(
        source: HTMLElement,
        container: HTMLElement,
        dir: SpatialNavigationDirection,
        fallbackDirs?: SpatialNavigationDirection[]
    ): HTMLElement | null {
        const candidates = container.focusableAreas({ mode: 'all' });
        let bestTarget = this.getClosestChildInternal(source, container, dir, candidates);
        if (!bestTarget && fallbackDirs?.length > 0) {
            for (const fallbackDir of fallbackDirs) {
                bestTarget = this.getClosestChildInternal(source, container, fallbackDir, candidates);
                if (bestTarget) {
                    break;
                }
            }
        }
        return bestTarget;
    }

    private getClosestChildInternal(
        source: HTMLElement,
        container: HTMLElement,
        dir: SpatialNavigationDirection,
        candidates: Node[]
    ): HTMLElement | null {
        return source.spatialNavigationSearch(dir, {
            container,
            candidates
        }) as HTMLElement;
    }

    /**
     * the function constructs an IntersectionObserver and passes the HTML element from parameters into its watch list.
     * then it returns an observable that continually emits a boolean value that represents if the element is fully
     * in the current viewport.
     *
     * @param elem the html element to be observed
     * @returns an observable emits a boolean value that indicates whether the target element
     * is fully in the current viewport.
     */
    isFullyInViewport(elem: Element): Observable<boolean> {
        return new Observable<boolean>(subscriber => {
            const observer = new IntersectionObserver(entries => {
                const entry = entries.filter(ent => ent.intersectionRatio < 1)?.[0];
                subscriber.next(!entry);
            });
            observer.observe(elem);
            return function unsubscribe() {
                observer.disconnect();
            };
        });
    }

    onClick(element: HTMLElement): Observable<MouseEvent | KeyboardEvent> {
        return defer(() => {
            if (element instanceof HTMLAnchorElement) {
                // If an <a> has click listener registered, its tabindex needs to be set to be focusable
                // should set tabIndex here, due to element which tabIndex is 0 by default and it's still unfocusable
                element.tabIndex = 0;
            }
            return merge(fromEvent(element, 'click'), fromEvent(element, 'keydown')).pipe(
                mergeMap((event: MouseEvent | KeyboardEvent) => {
                    if (event instanceof MouseEvent) {
                        return of(event);
                    } else if (event.key === 'Enter' || event.key === ' ') {
                        return of(event);
                    }
                    return of(undefined);
                })
            );
        });
    }

    isDefaultFocusRequired(): boolean {
        return this.enabled && !!this.config.isDefaultFocusRequired;
    }

    navigate(direction: SpatialNavigationDirection): void {
        this._navigate?.(direction);
    }

    /**
     * determine the element is navigation focusable
     *
     * @param element
     * @returns true if the element is navigation focusable, otherwise false
     *
     * The difference between spatial navigation's isFocusable and InterActivityChecker#isFocusable is that
     * InterActivityChecker#isFocusable consider elements with a valid tabIndex as focusable, spatial navigation
     * only considers elements with tabIndex greater or equal than zero as focusable.
     */
    isFocusable(element: HTMLElement): boolean {
        if (!this.spatialNavigation || !this.spatialNavigation.isFocusable) {
            const windowSpatialNavigation = window.__spatialNavigation__;
            this.logger.info(
                `this.spatialNavigation=${!!this.spatialNavigation}, this.spatialNavigation.isFocusable=${!!this
                    .spatialNavigation?.isFocusable}`
            );
            this.logger.info(
                `window.__spatialNavigation__=${!!windowSpatialNavigation}, window.__spatialNavigation__.isFocusable=${!!windowSpatialNavigation?.isFocusable}`
            );
            this.logger.info(
                `this.spatialNavigation===window.__spatialNavigation__ ? ${
                    this.spatialNavigation === windowSpatialNavigation
                }`
            );
            return false;
        }
        return this.spatialNavigation.isFocusable(element);
    }

    /**
     * Finds the next spatial navigation target.
     *
     * @param element
     * @param dir
     * @param container the element contains the candidates we want to search for
     * @returns the closest focusable from element in the direction of dir, returns null if not found.
     *
     */
    findNextTarget(element: HTMLElement, dir: SpatialNavigationDirection, container = document.body): HTMLElement {
        let x: number;
        let y: number;
        const rect = element.getBoundingClientRect();
        if (isHorizontalDir(dir)) {
            x = dir === 'left' ? rect.left - 1 : rect.right + 1;
            y = (rect.top + rect.bottom) / 2;
        } else {
            x = (rect.left + rect.right) / 2;
            y = dir === 'up' ? rect.top - 1 : rect.bottom + 1;
        }
        this.setStartingPointAt(x, y);
        return this.getClosestChild(document.body, container, dir);
    }

    // decide whether to contain focus in a container based on the navigate direction
    shouldContainFocus(dir: SpatialNavigationDirection, containFocusArgs: ContainFocusArgs) {
        if (Array.isArray(containFocusArgs)) {
            return containFocusArgs.includes(dir);
        }
        return containFocusArgs;
    }
}
